// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Responsive layout
//  ----------------------------------------------------------------------------
//  Until now the app was a 393×852 box inside a drawn iPhone bezel: a prototype
//  harness that shipped. This module is what replaces it.
//
//  The shape of the idea — the app does NOT get a second design on a big screen.
//  The phone layout IS the app; on a wide screen it becomes a PANEL pinned to
//  the leading edge, unchanged, and the width you gain becomes open space beside
//  it. The panel keeps its week strip, its floating "+", its long-press, all of
//  it, so there is one design and one set of gestures to maintain.
//
//      phone    the panel fills the screen. Nothing else exists.
//      tablet   a full board with the day floating over its leading edge. The
//               board keeps the whole viewport rather than accepting the thin
//               remainder beside a docked panel; folding the day away reveals
//               the complete drawing surface. This is an upright tablet only.
//      desktop  the panel at the leading edge, and the rest is the CANVAS: for
//               now the surface a record sheet opens onto, later a place to drop
//               records, connect them, pin a board or a web view.
//
//  A tablet turned SIDEWAYS is a desktop, not a wide tablet — see MD_LANDSCAPE_H.
//
//  The rule that follows from this, and the reason it stays simple: everything
//  the phone opens full-screen — the hour grid, Settings, Reschedule — keeps
//  opening full-screen INSIDE the day panel. A record sheet opens on the board
//  whenever one exists, whether the panel is docked or floating.
//
//  Exposes useMdLayout on window — the hook IS the API. The breakpoints and the
//  measurement behind it are private, so there is one answer to "what layout are
//  we in" rather than a constant others can re-derive it from.
//  Loaded right after md-theme so every later module can destructure it.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useStateL, useEffect: useEffectL } = React;

/* Breakpoints picked against real devices, not round numbers:
     phone    < 700     every iPhone upright (max 430pt), and the small ones
                        turned sideways — iPhone SE landscape is 667
     tablet   700–1099  iPad mini portrait 744, iPad 11" portrait 820,
                        iPad Pro 12.9" portrait 1024 — AND the big iPhones turned
                        sideways (852, 932), which are too short for a canvas
     desktop  ≥ 1100    iPad 11" landscape 1180, iPad Pro landscape 1366, laptops
   Width is not the whole rule: the landscape test below promotes a sideways
   tablet into `desktop` no matter which side of 1100 its width lands on. */
const MD_BP = { tablet: 700, desktop: 1100 };
/* A tablet held sideways gets the DESKTOP layout — panel plus canvas — whatever
   its width. Width alone put the two smaller iPads on the wrong side of the line:
   iPad 9.7"/Air landscape is 1024 and iPad 10.2" landscape is 1080, both under
   1100, so both showed a 420pt strip floating in the middle of a landscape screen
   with ~300pt of dead black either side. Sideways is precisely when the canvas has
   somewhere to live, so orientation decides it, not width.
   MD_LANDSCAPE_H is what separates a tablet turned sideways from a PHONE turned
   sideways: an iPhone 15 Pro landscape is 852×393 and an iPhone 15 Pro Max is
   932×430, both wide enough to pass any width test, and neither has the height to
   carry a panel beside a canvas. Every iPad in landscape is 744 or taller. */
const MD_LANDSCAPE_H = 600;
/* The panel's width. The phone design is tuned at 393pt with 16pt gutters, and
   this is that, plus a little air — close enough that every measurement inside
   still reads the way it was drawn, wide enough not to look like the old mockup. */
const MD_PANEL_W = 420;
const MD_CANVAS_MIN = 560;   // minimum remainder beside a docked desktop panel

function mdMeasureLayout() {
  // visualViewport is the honest number while a software keyboard is up; on
  // desktop it tracks the window. innerWidth is the fallback for old Safari.
  const vv = window.visualViewport;
  const w = Math.round(vv ? vv.width : window.innerWidth);
  const h = Math.round(vv ? vv.height : window.innerHeight);
  /* Orientation is read off the LAYOUT viewport, never off `h` above. A software
     keyboard shrinks visualViewport — an iPad in landscape goes 768 → ~400 the
     moment you tap into compose — and reading orientation from that number would
     drop the canvas and re-lay the whole window out mid-keystroke. innerHeight
     does not move for the keyboard, so the shape of the DEVICE stays the shape of
     the device while `h` still reports the honest space left to draw in. */
  const landscape = window.innerWidth > window.innerHeight &&
    window.innerHeight >= MD_LANDSCAPE_H;
  const mode = w >= MD_BP.desktop || (landscape && w >= MD_BP.tablet) ? 'desktop'
    : w < MD_BP.tablet ? 'phone' : 'tablet';
  /* The height guard excludes the large iPhones whose landscape width happens
     to sit in the tablet band. An upright iPad gets a board, but it gets the
     WHOLE board: docking a 420pt day beside it would leave too little room for
     the drawing toolbar and make both halves feel compromised. */
  const overlay = mode === 'tablet' && !landscape && window.innerHeight >= MD_BP.tablet;
  // A MOUSE, specifically — not "is it big". An iPad at 1180 gets the canvas but
  // keeps its swipe gestures, so hover affordances would be dead weight there.
  const fine = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  const phone = mode === 'phone';
  const panel = phone ? w : MD_PANEL_W;
  const dockedCanvas = mode === 'desktop' && w - panel >= MD_CANVAS_MIN;
  const canvas = overlay || dockedCanvas;
  return { w, h, mode, fine, phone, wide: !phone, panel, canvas, overlay,
    canvasW: canvas ? (overlay ? w : w - panel) : 0 };
}

function mdSameLayout(a, b) {
  return a.w === b.w && a.h === b.h && a.mode === b.mode && a.fine === b.fine &&
    a.panel === b.panel && a.canvas === b.canvas && a.overlay === b.overlay;
}

/* ── THE PAGE COMING BACK ────────────────────────────────────────────────────
   requestAnimationFrame is not a promise. iOS suspends a tab that goes to the
   background and DROPS the frames it had pending: they never run, and they are
   never cancelled. Every "measure at most once a frame" latch in this app is
   written the same way, because it is the correct way —

       if (frame != null) return;
       frame = requestAnimationFrame(() => { frame = null; … });

   — and every one of them has the same end. The dropped frame leaves the
   variable set for the life of the page, so the measurement never runs again,
   every later resize returns at the first line, and the screen keeps whatever
   it measured before it was put away. There are three of these, and on
   2026-08-31 they are half of why a desk came back from an iPad's home screen
   unable to scroll or draw.

   So coming back is a signal, and this is the one place that knows what it
   means. Two events, because one is not enough: `visibilitychange` for the
   home button and the app switcher, and `pageshow` for Safari's
   back-forward cache, which restores a page that was never hidden at all.
   Returns the way to stop listening. */
function mdOnPageResume(fn) {
  const wake = () => { if (document.visibilityState !== 'hidden') fn(); };
  document.addEventListener('visibilitychange', wake);
  window.addEventListener('pageshow', wake);
  return () => {
    document.removeEventListener('visibilitychange', wake);
    window.removeEventListener('pageshow', wake);
  };
}

/* One measurement shared by every caller. Re-renders only when something a
   layout actually depends on changed — a 1px scrollbar wobble must not re-run
   the whole tree, and on iOS the keyboard resizes visualViewport constantly. */
function useMdLayout() {
  const [L, setL] = useStateL(mdMeasureLayout);
  useEffectL(() => {
    let raf = null;
    const onResize = () => {
      if (raf != null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        setL((prev) => { const next = mdMeasureLayout(); return mdSameLayout(prev, next) ? prev : next; });
      });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', onResize);
    const mq = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)');
    if (mq && mq.addEventListener) mq.addEventListener('change', onResize);
    /* The frame we are still holding is the one that will never arrive. Let it
       go and measure once — the viewport this page comes back to is rarely the
       one it left, and until this ran nothing else would ever ask. */
    const resumed = mdOnPageResume(() => {
      if (raf != null) { cancelAnimationFrame(raf); raf = null; }
      onResize();
    });
    return () => {
      if (raf != null) cancelAnimationFrame(raf);
      resumed();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (vv) vv.removeEventListener('resize', onResize);
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onResize);
    };
  }, []);
  return L;
}

Object.assign(window, { useMdLayout, mdOnPageResume });
